// crewbit-admin/src/lib/scheduleErrorMap.ts
//
// Schedule Module Phase B.1 (§T.93) — error namespaces for the 11 Schedule RPCs +
// the 11 Schedule cross-cutting defaults. Mirror of mobile's errorMessagesSchedule.ts;
// bodies byte-identical (per feedback_admin_pair_must_match_mobile_body.md).
//
// errorMap.ts spreads `scheduleErrorNamespaces` into its `errorMessagesByRpc` table +
// `scheduleDefaultErrorMessages` into its `defaultErrorMessages` record + re-exports
// the type symbols defined here. Hook authors and wrapper consumers continue to
// import via `'./errorMap'` for type symbols + `lookupErrorCopy`.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §3.

import type { MoneyErrorCode } from './errorMap';

/** Stable error codes returned by Schedule RPCs (DATA spec §C, A11 + A13). */
export type ScheduleErrorCode =
  // Cross-cutting infra (semantically shared with MoneyErrorCode; kept in
  // separate union for type-clarity in Schedule consumer hooks).
  | 'NO_ACTIVE_BUSINESS'
  | 'STALE_PUBLIC_USER_ID'
  | 'NETWORK_FAILURE'
  | 'AUTH_EXPIRED'
  | 'RLS_DENIED'
  | 'UNKNOWN'
  // Schedule-specific (DATA spec §C)
  | 'ENTRY_NOT_FOUND'
  | 'SHIFT_NOT_FOUND'
  | 'EDIT_VIA_SHIFT_RPC'
  | 'OPERATOR_NOT_ASSIGNED'
  | 'NOT_AUTHORIZED_TO_REASSIGN'
  | 'OPERATOR_CANNOT_REASSIGN'
  | 'OPERATOR_NOT_AUTHORIZED_TO_CREATE_FOR_PEERS'
  | 'NOT_AUTHORIZED_TO_CREATE_SHIFT'
  | 'CONFLICT_DETECTED'
  | 'INVALID_TYPE'
  | 'INVALID_TIME_RANGE'
  // §Schedule.PhaseC.C1 — update_user_schedule_preferences (M-Schedule-C0).
  | 'INVALID_VIEW'
  // §Schedule.WalkInQueueSubstrateSplit C5A — create_walk_in_pending + assign_walk_in.
  | 'WALK_IN_NOT_FOUND'
  | 'WALK_IN_ALREADY_ASSIGNED'
  | 'BUSINESS_MISMATCH'
  // §WorldClass.6.FieldOpsCompound C7 — compound feature RPCs.
  | 'NOT_OWNER'
  | 'RADIUS_OUT_OF_BOUNDS'
  | 'BOOKING_NOT_FOUND'
  | 'NO_CLIENT_PHONE'
  | 'INVALID_DISTANCE';

/** Detail row inside `ScheduleError.conflicts[]` when code is CONFLICT_DETECTED. */
export type ConflictDetail = {
  type: 'double_booking' | 'resource_double_book' | 'overtime' | 'rest_period';
  severity: 'warning' | 'error';
  description?: string;
  conflicting_entry_id?: string;
  assignee_id?: string;
  total_hours?: number;
  threshold?: number;
};

export type ScheduleError = {
  code: ScheduleErrorCode;
  /** Internal description for Sentry — never shown to user. */
  message?: string;
  /** Optional: which input field the error relates to. Drives inline vs toast placement. */
  field?: string;
  /** Only present on CONFLICT_DETECTED. Carries the conflict array from `detect_schedule_conflicts`. */
  conflicts?: ConflictDetail[];
  /** Only present on EDIT_VIA_SHIFT_RPC — points caller at the parent `schedule_shifts.id`. */
  shift_id?: string;
  meta?: Record<string, unknown>;
};

/** Per-RPC error copy for the 11 Schedule RPCs (WIRING spec §3.2). */
export const scheduleErrorNamespaces: Record<string, Partial<Record<MoneyErrorCode | ScheduleErrorCode, string>>> = {
  create_schedule_entry: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then create schedule entries.",
    STALE_PUBLIC_USER_ID: "Your account needs to refresh — sign out and back in.",
    INVALID_TYPE: "That entry type isn't supported. Pick one of: job, booking, event, personal, block, class.",
    INVALID_TIME_RANGE: "End time must be after start time.",
    EDIT_VIA_SHIFT_RPC: "Shifts are created via Time-off & Cover, not the schedule canvas.",
    OPERATOR_NOT_AUTHORIZED_TO_CREATE_FOR_PEERS: "You can only create entries assigned to yourself. Ask your manager to assign work to teammates.",
    CONFLICT_DETECTED: "There's a conflict with another entry. Review and confirm to override.",
  },
  update_schedule_entry: {
    ENTRY_NOT_FOUND: "That entry no longer exists.",
    EDIT_VIA_SHIFT_RPC: "This entry is a shift. Edit it in Time-off & Cover.",
    OPERATOR_NOT_ASSIGNED: "You can only edit entries assigned to you.",
    INVALID_TIME_RANGE: "End time must be after start time.",
    CONFLICT_DETECTED: "There's a conflict with another entry. Review and confirm to override.",
  },
  delete_schedule_entry: {
    ENTRY_NOT_FOUND: "That entry no longer exists.",
    EDIT_VIA_SHIFT_RPC: "This entry is a shift. Delete it in Time-off & Cover.",
    OPERATOR_NOT_ASSIGNED: "You can only delete entries assigned to you.",
  },
  reassign_schedule_entry: {
    ENTRY_NOT_FOUND: "That entry no longer exists.",
    EDIT_VIA_SHIFT_RPC: "This entry is a shift. Reassign it in Time-off & Cover.",
    OPERATOR_CANNOT_REASSIGN: "Reassignment is owner/manager only. Ask your manager to reassign this entry.",
    NOT_AUTHORIZED_TO_REASSIGN: "Only owners and managers can reassign schedule entries.",
    CONFLICT_DETECTED: "The new assignee already has a conflicting entry. Review and confirm to override.",
  },
  reschedule_schedule_entry: {
    ENTRY_NOT_FOUND: "That entry no longer exists.",
    EDIT_VIA_SHIFT_RPC: "This entry is a shift. Reschedule it in Time-off & Cover.",
    OPERATOR_NOT_ASSIGNED: "You can only reschedule entries assigned to you.",
    INVALID_TIME_RANGE: "End time must be after start time.",
    CONFLICT_DETECTED: "The new time conflicts with another entry. Review and confirm to override.",
  },
  create_schedule_shift: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then create shifts.",
    NOT_AUTHORIZED_TO_CREATE_SHIFT: "Only owners and managers can create shifts.",
    INVALID_TIME_RANGE: "End time must be after start time.",
    CONFLICT_DETECTED: "This shift conflicts with existing work. Review and confirm to override.",
  },
  update_schedule_shift: {
    SHIFT_NOT_FOUND: "That shift no longer exists.",
    CONFLICT_DETECTED: "The new shift time conflicts with existing work. Review and confirm to override.",
  },
  delete_schedule_shift: {
    SHIFT_NOT_FOUND: "That shift no longer exists.",
  },
  // create_public_booking: id-variant (admin walk-in lift, B2/B3). Slug-variant is
  // anon-callable from /src/ marketing site and out of scope for the wrapper.
  create_public_booking: {
    NO_ACTIVE_BUSINESS: "Pick a business first to book this service.",
    INVALID_TIME_RANGE: "Pick a valid start time.",
    CONFLICT_DETECTED: "That time slot is no longer available. Pick another.",
  },
  // resolve_schedule_view + detect_schedule_conflicts are read-only helpers;
  // they only emit infra codes (NO_ACTIVE_BUSINESS / STALE_PUBLIC_USER_ID / RLS_DENIED).
  // Default copy from defaultErrorMessages covers them; no per-RPC override needed.

  // §Schedule.PhaseC.C1 — Settings → Schedule defaults picker save path (M-Schedule-C0).
  update_user_schedule_preferences: {
    STALE_PUBLIC_USER_ID: "Sign out and back in, then try again.",
    INVALID_VIEW: "Pick a valid default zoom/lens option from the list.",
  },

  // §Schedule.WalkInQueueSubstrateSplit C5A — walk-in queue 2-step flow (M-Schedule-C5).
  create_walk_in_pending: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then queue a walk-in.",
    BUSINESS_MISMATCH: "Walk-in business doesn't match your active business.",
  },
  assign_walk_in: {
    WALK_IN_NOT_FOUND: "That walk-in is no longer in the queue.",
    WALK_IN_ALREADY_ASSIGNED: "Someone else already assigned this walk-in.",
    BUSINESS_MISMATCH: "Walk-in belongs to a different business.",
    CONFLICT_DETECTED: "That slot conflicts with another entry. Review and confirm to override.",
  },

  // §WorldClass.6.FieldOpsCompound C7 — compound feature RPCs.
  update_compound_feature_settings: {
    NOT_OWNER: "Only business owners can change auto-feature settings.",
    RADIUS_OUT_OF_BOUNDS: "Geofence radius must be between 50 and 500 metres.",
  },
  record_geofence_arrival: {
    ENTRY_NOT_FOUND: "That schedule entry no longer exists.",
    BUSINESS_MISMATCH: "That entry doesn't belong to your business.",
  },
  record_mileage_segment: {
    ENTRY_NOT_FOUND: "That schedule entry no longer exists.",
    INVALID_DISTANCE: "Distance must be zero or greater.",
  },
  enqueue_arrival_notification: {
    BOOKING_NOT_FOUND: "That booking no longer exists.",
    NO_CLIENT_PHONE: "Client has no phone number on file.",
    BUSINESS_MISMATCH: "That booking doesn't belong to your business.",
  },
  revoke_background_gps_consent: {
    STALE_PUBLIC_USER_ID: "Sign out and back in, then try again.",
  },
  grant_background_gps_consent: {
    STALE_PUBLIC_USER_ID: "Sign out and back in, then try again.",
  },
  update_navigate_confirm_pref: {
    STALE_PUBLIC_USER_ID: "Sign out and back in, then try again.",
  },
};

/**
 * Cross-cutting Schedule defaults — Schedule-specific codes that fall through the
 * per-RPC namespace. errorMap.ts merges these into its `defaultErrorMessages`
 * record via spread.
 */
export const scheduleDefaultErrorMessages: Record<ScheduleErrorCode, string> = {
  NETWORK_FAILURE: "No connection. Try again when you're back online.",
  AUTH_EXPIRED: "Your session expired. Sign in again to continue.",
  RLS_DENIED: "You don't have access to that.",
  NO_ACTIVE_BUSINESS: "Pick a business first, then try again.",
  STALE_PUBLIC_USER_ID: "Your account needs to refresh. Sign out and back in, then try again.",
  UNKNOWN: "Something went wrong. Try again, or contact support if it keeps happening.",
  ENTRY_NOT_FOUND: "That schedule entry no longer exists.",
  SHIFT_NOT_FOUND: "That shift no longer exists.",
  EDIT_VIA_SHIFT_RPC: "This entry is a shift. Manage it in Time-off & Cover.",
  OPERATOR_NOT_ASSIGNED: "You can only manage entries assigned to you.",
  NOT_AUTHORIZED_TO_REASSIGN: "Only owners and managers can reassign schedule entries.",
  OPERATOR_CANNOT_REASSIGN: "Reassignment is owner/manager only. Ask your manager.",
  OPERATOR_NOT_AUTHORIZED_TO_CREATE_FOR_PEERS: "You can only create entries assigned to yourself.",
  NOT_AUTHORIZED_TO_CREATE_SHIFT: "Only owners and managers can create shifts.",
  CONFLICT_DETECTED: "There's a conflict with another entry. Review and confirm to override.",
  INVALID_TYPE: "That entry type isn't supported.",
  INVALID_TIME_RANGE: "End time must be after start time.",
  INVALID_VIEW: "Pick a valid default zoom/lens option.",
  WALK_IN_NOT_FOUND: "That walk-in is no longer in the queue.",
  WALK_IN_ALREADY_ASSIGNED: "Someone else already assigned this walk-in.",
  BUSINESS_MISMATCH: "That doesn't belong to your active business.",
  NOT_OWNER: "Only business owners can change this setting.",
  RADIUS_OUT_OF_BOUNDS: "Geofence radius must be between 50 and 500 metres.",
  BOOKING_NOT_FOUND: "That booking no longer exists.",
  NO_CLIENT_PHONE: "Client has no phone number on file.",
  INVALID_DISTANCE: "Distance must be zero or greater.",
};
